import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  AccountTree as AccountTreeIcon,
  TextFields as TextFieldsIcon,
  Notes as NotesIcon,
  Numbers as NumbersIcon,
  DateRange as DateRangeIcon,
  FileUpload as FileUploadIcon,
  ListAlt as ListAltIcon,
  PlayArrow as PlayArrowIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Category as CategoryIcon,
  Event as EventIcon,
  Receipt as ReceiptIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  Image as ImageIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CallSplit as CallSplitIcon,
  QuestionMark as QuestionMarkIcon,
  Approval as ApprovalIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  Transform as TransformIcon,
} from '@mui/icons-material';
// `@hello-pangea/dnd`, NOT `react-beautiful-dnd` — and this is a correctness fix, not an upgrade.
//
// THE DEFECT (owner, 2026-09-01, twice): every drag threw
//
//     Invariant failed: Cannot find droppable entry with id [canvas]
//
// `react-beautiful-dnd@13.1.1` was archived in 2022 and does not support React 18's StrictMode,
// which this app uses (`index.tsx` wraps the tree in `<React.StrictMode>` under React 18.3.1).
// StrictMode double-invokes effects in development — mount, unmount, mount — and rbd 13's Droppable
// unregisters on that cleanup without re-registering against the live registry. The component stays
// mounted while the registry is empty, so the FIRST drag asks for `canvas` and finds nothing.
//
// It is development-only (a production build does not double-invoke), which is why it survived: the
// deployed app drags fine and nobody had dragged locally. It also has nothing to do with the change
// that preceded the report — I made the droppable unconditional first, on a real but different
// fragility, and the error persisted unchanged. That persistence is what ruled the structure out.
//
// `@hello-pangea/dnd` is rbd's maintained fork by its own community, with the SAME public API and
// React 18/19 in its peer range. The migration is the import specifier and nothing else — no prop,
// no callback, no id changes — which is why it is safe to do in one commit.
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

import { DESIGNER_STAGE_HEIGHT, PORTAL_FORM_MAX_WIDTH } from '../../../../theme/shellMetrics';
import {
  FormComponent,
  FormFlowStep,
  FormTemplate,
} from '../types/formTypes';
import { processesApi } from '../../../../api';
import type {
  FormComponent as LiveFormComponent,
  FormTemplate as LiveFormTemplate,
} from '../../../../api/processes';
import {
  getFormComponentDisplay,
  getFormTemplateDisplay,
  translateFormComponentCode,
  translateTemplateDomain,
  type ProcessTranslator,
} from '../../processDisplay';
import FormOptionsEditor from './FormOptionsEditor';
import { canvasRowText } from './formFieldNaming';
import { CANVAS_ROW } from '../../../../theme/shellMetrics';
import {
  mintGroupStamp,
  toCanvasBlocks,
  groupMemberIds,
  reorderBlocks,
} from '../formGroups';

import {
  PROCESS_ATTACHMENT_FORMATS,
  attachmentFormats,
} from '../../../WorkerPortal/processFormContract';



// Re-export types for parent usage
export type { FormComponent, FormFlowStep } from '../types/formTypes';

/**
 * Palette | canvas | properties — and the properties column is CONDITIONAL.
 *
 * History, because this has now gone both ways. The library began as a permanently docked
 * 240px column. Slice E replaced it with a modal Drawer on width arithmetic:
 * `240 | 1fr | 260` plus two 12px gaps is 524px of fixed width, so on the 13" baseline
 * (1280 viewport → 976 usable) the canvas got 452px — narrower than the 720px form it
 * renders. The owner rejected the drawer outright ("煞笔设计", 2026-08-24): it covered the
 * app's own top nav, dimmed the canvas behind a backdrop, and made picking a field a modal
 * task instead of a glanceable list. Their earlier docked layout and the competitor's
 * (a compact two-column palette pinned to the left) are the shape they want.
 *
 * The arithmetic that makes docking work is the properties column, not the palette:
 *
 *   nothing selected   240 + 12 + canvas                → canvas 724px  (clears the 720 form)
 *   field selected     240 + 12 + canvas + 12 + 260      → canvas 452px
 *
 * Properties are only meaningful once a field is selected, and while editing one the user is
 * looking at that field rather than reading the whole form — so the narrow case is the case
 * that can afford to be narrow. This is also what the competitor does: their palette is
 * always visible and their inspector is not.
 *
 * The palette itself is a two-column grid of compact chips rather than 13 full-width rows,
 * which is what lets 240px hold the whole catalogue without scrolling on the baseline.
 *
 * THE CANVAS COLUMN IS CAPPED, and at the number this comment already named: 724px was chosen
 * because it "clears the 720 form", so 720 was always the target — the baseline just happened to
 * produce it. On a large display nothing produced it: the track is `1fr`, so at ~2300px the canvas
 * grew to ~1700px and a row's label sat ~1900px from its own type summary.
 *
 * The cap belongs HERE, on the column, and not on the droppable inside the panel. Capping the inner
 * grid was tried first and was worse than no cap: the PANEL still filled the track, so the canvas
 * sat in the left 720px of a 1720px panel with a thousand pixels of empty card beside it and the
 * header's "N components" chip stranded at the far right, a thousand pixels from what it counts.
 *
 * The trailing `1fr` is slack. It takes the leftover so the three real columns pack LEFT, which is
 * the alignment the owner confirmed for the Basics and Review steps — rather than leaving a gap
 * between the canvas and the properties panel, or floating the properties panel mid-screen.
 *
 * On the 13" baseline nothing changes: `minmax(0, 720px)` yields 720 with 4px of slack when nothing
 * is selected, and 452 with zero slack when a field is, exactly as the arithmetic above describes.
 */
const FORM_BUILDER_COLUMNS = {
  xs: 'minmax(0, 1fr)',
  md: `240px minmax(0, ${PORTAL_FORM_MAX_WIDTH}px) 1fr`,
} as const;
const FORM_BUILDER_COLUMNS_WITH_PROPERTIES = {
  xs: 'minmax(0, 1fr)',
  md: `240px minmax(0, ${PORTAL_FORM_MAX_WIDTH}px) 260px 1fr`,
} as const;

/** Convert live API FormComponent to the local shape used by the canvas. */
function toLegacy(c: LiveFormComponent, t: ProcessTranslator): FormComponent {
  const propertyDescriptors = Array.isArray(c.properties) ? c.properties : [];
  const runtimeProperties = c.properties && typeof c.properties === 'object' && !Array.isArray(c.properties)
    ? c.properties as Record<string, unknown>
    : {};
  const display = getFormComponentDisplay(t, {
    isSystem: (c as any).is_system ?? false,
    name: c.name,
    label: c.label,
    category: c.category ?? 'Basic',
    description: c.description,
    properties: propertyDescriptors as any[],
  });
  const defaults = Object.fromEntries(display.properties
    .filter((property) => property.defaultValue !== undefined)
    .map((property) => [
      property.name,
      property.name === 'options' && typeof property.defaultValue === 'string'
        ? property.defaultValue.split('\n')
        : property.defaultValue,
    ]));
  const icon = c.field_type === 'file_attachment'
    ? 'FileUpload'
    : c.field_type === 'organization_location'
      ? 'LocationOnIcon'
      : c.field_type === 'date_picker'
        ? 'DateRange'
        : c.field_type === 'number_field' || c.field_type === 'currency_field'
          ? 'Numbers'
          : 'TextFields';
  return {
    id: c.id,
    persistedComponentId: c.id,
    name: c.name,
    fieldType: c.field_type,
    displayName: display.label,
    description: display.description,
    // Carried through so the canvas can tell "labelled with its own type name" (a seeded
    // built-in, which says nothing) from "labelled by the admin" (a custom component,
    // whose label is the whole point). Dropping it here is what made every single-word
    // custom label read as "Untitled field".
    isSystem: (c as any).is_system ?? false,
    icon,
    properties: {
      label: display.label,
      required: false,
      // NO `placeholder` here. It was injected onto EVERY type, so a Date, Dropdown,
      // Location, Worker, Department or Guidance Note all offered a Placeholder editor for
      // a concept that does not apply to them — the last loose thread of audit finding 1.
      //
      // The catalog declares it on exactly the two types whose renderers read it
      // (`text_field`, `textarea_field`), and `...defaults` below supplies it from there.
      // Since defaults already WON over this line, removing it changes nothing for those
      // two and removes the key everywhere else, which is what the `hasOwnProperty` gate
      // then hides (2026-08-25).
      colSpan: 12,
      ...defaults,
      ...runtimeProperties,
    },
  };
}

interface ProcessFormDesignProps {
  formComponents: FormFlowStep[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onAdd: (component: FormComponent) => void;
  onRemove: (id: string) => void;
  /** Whole-group delete. A grouped field has no delete of its own (owner 2026-08-27),
   *  so the canvas needs a way to withdraw every member at once. */
  onRemoveMany: (ids: string[]) => void;
  /** Block-aware reorder. `onDragEnd` moved exactly ONE flat element, which tears a
   *  group apart on the first drag, so the builder computes the whole next order. */
  onReorderSteps: (nextOrder: string[]) => void;
  onUpdate: (id: string, key: string, value: any) => void;
  /** A template card was clicked and it resolved to NO components.
   *
   * Optional so existing call sites keep working, but the designer supplies it — a click that does
   * nothing at all is worse than an explanation. See `addFormTemplate`. */
  onEmptyTemplate?: (template: FormTemplate) => void;
  /** A template was inserted, with how many fields it added.
   *
   * The insert appends to the end of the canvas, so on a non-empty form the result is off-screen.
   * Without this the admin has no evidence the click did anything. */
  onTemplateInserted?: (template: FormTemplate, count: number) => void;
}

const ProcessFormDesign: React.FC<ProcessFormDesignProps> = ({
  formComponents,
  selectedId,
  onSelect,
  onAdd,
  onEmptyTemplate,
  onTemplateInserted,
  onRemove,
  onUpdate,
  onRemoveMany,
  onReorderSteps,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [libraryTab, setLibraryTab] = useState(0);
  /** The library is a drawer now; two docked columns squeezed the canvas to 432px. */
  const [hoveredComponentId, setHoveredComponentId] = useState<string | null>(null);
  const [liveComponents, setLiveComponents] = useState<FormComponent[]>([]);
  const [liveTemplates, setLiveTemplates] = useState<FormTemplate[]>([]);

  // Load form components + templates from live API on mount
  useEffect(() => {
    Promise.all([
      processesApi.formComponentsApi.list(),
      processesApi.formTemplatesApi.list(),
    ]).then(([cs, ts]) => {
      const comps = cs.map((component) => toLegacy(component, t));
      setLiveComponents(comps);
      const idToComp = new Map(comps.map(c => [c.id, c]));
      setLiveTemplates((ts as LiveFormTemplate[]).map((template): FormTemplate => {
        // ORDER and WIDTH come from the template now (migration 111). Previously this read
        // a bare id array, so a template could only ever be expanded into full-width fields
        // in whatever order the ids happened to sit — which is why an inserted template did
        // not look like the form the admin had designed.
        //
        // The span is folded onto the component's `properties.colSpan`, the same key the
        // canvas already reads for a loose field, so nothing downstream needs to know a
        // template was involved.
        const templateItems: Array<{ component_id: string; col_span: number }> =
          Array.isArray(template.items)
            ? (template.items as Array<{ component_id: string; col_span?: number }>)
              .map((item) => ({ component_id: item.component_id, col_span: item.col_span ?? 12 }))
            : [];
        const resolvedComponents = templateItems
          .map((item): FormComponent | undefined => {
            const component = idToComp.get(item.component_id);
            if (!component) return undefined;
            return {
              ...component,
              properties: { ...component.properties, colSpan: item.col_span },
            };
          })
          .filter((c): c is FormComponent => !!c);
        const display = getFormTemplateDisplay(t, {
          isSystem: (template as any).is_system ?? false,
          name: template.name,
          description: template.description,
          category: template.category ?? 'General',
          difficulty: template.difficulty ?? 'Medium',
        });
        return {
          id: template.id,
          name: template.name,
          displayName: display.name,
          description: display.description,
          icon: 'ListAlt',
          category: template.category ?? 'General',
          components: resolvedComponents,
        } as unknown as FormTemplate;
      }));
    }).catch(() => {
      setLiveComponents([]);
      setLiveTemplates([]);
    });
  }, [t]);

  /** Is a drag running right now?
   *
   * A REF, not state, and that is load-bearing: setting state from `onDragStart` would re-render the
   * dragged subtree, which is the exact thing this exists to prevent (session-discipline §11 — a ref
   * for an "are we mid-X" latch).
   *
   * WHY IT IS NEEDED. `@hello-pangea/dnd` throws `Cannot find droppable entry with id [canvas]` from
   * `shouldPublishUpdate`, which runs when a Draggable is ADDED OR REMOVED DURING A DRAG — its own
   * warning on the next line says exactly that. `draggableId={block.key}` and `toCanvasBlocks`
   * derives that key from the step order, so any re-render that re-derives the blocks mid-drag
   * churns the registered ids. A click on a group member fires on MOUSE-UP, which for a drag begun on
   * that row lands inside the drag lifecycle — so selecting a field was re-rendering the canvas from
   * inside the drag. Reported twice, 2026-09-01. */
  const draggingRef = useRef(false);
  const selectedComponentData = formComponents.find((c) => c.id === selectedId) ?? null;
  /** The template this selected field came from, or `null` for a loose field.
   *
   * Drives two things the 2026-08-28 objections require: the panel says WHERE the field came from —
   * so an admin editing "Dropdown" inside "From IT Access Request" knows the change reaches THIS
   * form and not the template — and the WIDTH control is withheld, because `colSpan` is a property
   * of the template ROW and editing it here would make one decision settable in two places with no
   * way to see which won. */
  const selectedGroupLabel = selectedComponentData
    ? (typeof selectedComponentData.properties?.group_label === 'string'
      && selectedComponentData.properties.group_label !== ''
      ? selectedComponentData.properties.group_label as string
      : null)
    : null;

  const getIconComponent = useCallback((iconName: string): React.ReactElement => {
    switch (iconName) {
      case 'TextFields': return <TextFieldsIcon />;
      case 'Notes': return <NotesIcon />;
      case 'Numbers': return <NumbersIcon />;
      case 'DateRange': return <DateRangeIcon />;
      case 'FileUpload': return <FileUploadIcon />;
      case 'FormIcon': return <ListAltIcon />;
      case 'CheckIcon': return <CheckIcon />;
      case 'NodeIcon': return <AccountTreeIcon />;
      case 'PlayIcon': return <PlayArrowIcon />;
      case 'ProcessIcon': return <AccountTreeIcon />;
      case 'InfoIcon': return <InfoIcon />;
      case 'TimelineIcon': return <TimelineIcon />;
      case 'SettingsIcon': return <SettingsIcon />;
      case 'GroupIcon': return <GroupIcon />;
      case 'AssignmentIcon': return <AssignmentIcon />;
      case 'CategoryIcon': return <CategoryIcon />;
      case 'Event': return <EventIcon />;
      case 'Receipt': return <ReceiptIcon />;
      case 'RadioButtonChecked': return <RadioButtonCheckedIcon />;
      case 'ImageIcon': return <ImageIcon />;
      case 'AttachMoneyIcon': return <AttachMoneyIcon />;
      case 'PersonIcon': return <PersonIcon />;
      case 'LocationOnIcon': return <LocationOnIcon />;
      case 'HourglassEmptyIcon': return <HourglassEmptyIcon />;
      case 'CallSplitIcon': return <CallSplitIcon />;
      case 'QuestionMarkIcon': return <QuestionMarkIcon />;
      case 'ApprovalIcon': return <ApprovalIcon />;
      case 'NotificationsIcon': return <NotificationsIcon />;
      case 'ScheduleIcon': return <ScheduleIcon />;
      case 'TransformIcon': return <TransformIcon />;
      default: return <ListAltIcon />;
    }
  }, []);

  const addFormTemplate = (template: FormTemplate) => {
    // ONE group per insert. `addFormComponent` in the parent spreads
    // `...component.properties` onto the new step, so stamping here is all it takes —
    // no parent change, and `serializeFormSteps` passes `properties` through whole, so
    // the three keys persist without a serializer or backend change.
    //
    // AN EMPTY TEMPLATE MUST SAY SO. `resolvedComponents` above drops any item whose
    // `component_id` is not in the live component list — a `.filter(Boolean)` with no else — so a
    // template referencing a deleted component arrives here with zero components and this loop
    // runs zero times. The card visibly does nothing, which reads as "点了没反应" and is
    // indistinguishable from a broken click handler.
    //
    // Reported 2026-09-01. The data was fine in every tenant when checked (0 unresolvable refs
    // across 18 templates), so this is the backstop rather than the fix — but a click that
    // silently no-ops is the worst possible failure for a card whose whole affordance is being
    // clickable, and it costs one branch to rule out (session-discipline §12).
    if (template.components.length === 0) {
      onEmptyTemplate?.(template);
      return;
    }
    const stamp = mintGroupStamp(template);
    template.components.forEach((component) => onAdd({
      ...component,
      properties: { ...component.properties, ...stamp },
    }));
    // SAY THAT IT LANDED. The group appends at the END of the canvas, which on a form that already
    // has fields is below the fold — so the only evidence of a successful insert was a scroll away,
    // and the owner read two successful inserts as a dead button (2026-09-01).
    onTemplateInserted?.(template, template.components.length);
  };

  const getDisplayProperties = (step: FormFlowStep): Record<string, any> => {
    const raw = step.properties;
    return {
      ...raw,
      label: step.display?.label ?? raw.label,
      ...(step.display?.options ? { options: step.display.options } : {}),
    };
  };

  /**
   * ONE compact row per field — closer to a table than to a form.
   *
   * The canvas used to render a full mock input per field (label above, outlined control
   * below), which made every row ~90px tall: eight fields no longer fitted on a 13" screen and
   * the owner read it as loose (2026-08-23). Fidelity is not this panel's job — `Review` now
   * shows the real read-only renderer (`ProcessFormPreview`), so the canvas can do the job it
   * is actually for: see the order, see the shape, click one to edit it.
   */
  const renderCanvasComponent = (step: FormFlowStep, index: number, grouped: boolean) => {
    const properties = getDisplayProperties(step);
    const fieldType = step.component.fieldType ?? 'text_field';
    const optionCount = Array.isArray(properties.options) ? properties.options.length : 0;
    // BARE, no choice-count suffix: this string is compared against the label, and
    // "Dropdown · 4 choices" would never equal "Dropdown" so the comparison would
    // silently stop working for exactly the types that carry options.
    const typeCaption = translateFormComponentCode(t, fieldType);
    const row = canvasRowText({
      label: (properties.label as string) || (properties.title as string),
      componentName: step.component?.name,
      isSystem: step.component?.isSystem,
      typeCaption,
      grouped,
      untitledLabel: t('processes:wizardForm.untitledField'),
    });

    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, width: '100%' }}>
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ width: 18, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        >
          {index + 1}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* WHAT this prints is `canvasRowText`'s decision, not this file's — see that
              function for the one principle (a row never prints the same string twice) and
              for why a loose row and a group member resolve it differently. */}
          <Typography
            variant="body2"
            noWrap
            title={row.placeholder ? undefined : row.label}
            sx={{ fontWeight: 500, ...(row.placeholder ? { color: 'text.disabled', fontStyle: 'italic' } : {}) }}
          >
            {row.label}
            {properties.required && <Typography component="span" color="error.main"> *</Typography>}
          </Typography>
        </Box>
        {/* SHRINKABLE, and capped at half the row. It was `flexShrink: 0`, so in a
            half-width cell this caption and the width chip took every pixel and the
            LABEL — the only part the admin authored — was the one crushed to nothing:
            the owner's 17:26 screenshot shows a member rendered as "2  C
            Organization location", where "C" is a whole label at ~10px. Between a
            truncated type and a truncated label, the type is the one the row can
            afford to lose, and `title` keeps it readable on hover.

            NO WIDTH CHIP. It used to render `wizardForm.halfWidth` here, which
            re-stated what the canvas already draws — both the outer block grid
            (`gridColumn: span ${span}`) and a group's inner grid lay a half-width
            field out at half width, so the field is VISIBLY beside its neighbour.
            A ~72px badge restating that, inside the narrowest cell on the surface,
            is what left the label with no room. */}
        {row.typeCaption !== null && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={row.typeCaption}
            sx={{ flexShrink: 1, minWidth: 0, maxWidth: '50%' }}
          >
            {row.typeCaption}
            {optionCount > 0 && ` · ${t('processes:wizardForm.choiceCount', { count: optionCount })}`}
          </Typography>
        )}
      </Stack>
    );
  };


  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: selectedComponentData ? FORM_BUILDER_COLUMNS_WITH_PROPERTIES : FORM_BUILDER_COLUMNS, gap: 1.5, alignItems: 'start', minHeight: 0 }}>
        {/* DOCKED, not a drawer. See FORM_BUILDER_COLUMNS for the arithmetic that makes
            240px affordable and for why the drawer was rejected. */}
        <Paper
          sx={{
            height: { xs: 'auto', md: DESIGNER_STAGE_HEIGHT.height },
            minHeight: { xs: 'auto', md: DESIGNER_STAGE_HEIGHT.minHeight },
            borderRadius: 2, border: '1px solid', borderColor: 'divider',
            boxShadow: 'none', p: 1.5, display: 'flex', flexDirection: 'column',
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', flexShrink: 0 }}>
              {t('processes:wizardForm.componentLibrary')}
            </Typography>
            <Tabs value={libraryTab} onChange={(_, v) => setLibraryTab(v)} sx={{ mb: 1.5, minHeight: 36, flexShrink: 0 }} variant="fullWidth">
              <Tab label={t('processes:wizardForm.components')} sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }} />
              <Tab label={t('processes:wizardForm.templates')} sx={{ minHeight: 36, textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }} />
            </Tabs>
            <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, pr: { xs: 0, md: 0.5 }, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-track': { backgroundColor: theme.palette.grey[100], borderRadius: '3px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[400], borderRadius: '3px' } }}>
                {/* ONE column, and it is a measurement rather than a preference. The
                    competitor's palette is two columns because its labels are Chinese —
                    单行文本 is 4 characters. Ours are translated into nine locales, where the
                    longest is 'Ubicación de la organización' at 28; a 2-column chip inside a
                    240px panel holds about 10 latin characters, so every long label wrapped
                    to two or three lines (owner: "左侧ui已经超了，非常丑", 2026-08-24).
                    13 compact rows at ~34px fit the 500px minimum stage height. */}
              {libraryTab === 0 ? (
                <Box sx={{ mt: '2px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {liveComponents.map((component: FormComponent) => (
                    <Box
                      key={component.id}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1, py: 0.5, borderRadius: 1, minWidth: 0,
                        border: `1px solid ${theme.palette.divider}`,
                        cursor: 'grab', userSelect: 'none', transition: 'background-color 80ms ease, border-color 80ms ease',
                        '&:hover': { bgcolor: 'grey.50', borderColor: theme.palette.primary.main },
                        '&:active': { cursor: 'grabbing' },
                      }}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/json', JSON.stringify(component));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onClick={() => onAdd(component)}
                    >
                      <Box sx={{ width: 28, height: 28, borderRadius: 1, flexShrink: 0, bgcolor: `${theme.palette.primary.main}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', '& svg': { fontSize: '1rem' } }}>
                        {getIconComponent(component.icon)}
                      </Box>
                      <Typography
                        variant="body2"
                        noWrap
                        title={component.displayName ?? component.name}
                        /* noWrap is the backstop: the longest label measures 28 characters
                           and the row holds about 28, so a future locale must truncate
                           rather than silently reflow the whole palette. */
                        sx={{ fontWeight: 500, fontSize: '0.8125rem', flex: 1, minWidth: 0 }}
                      >
                        {component.displayName ?? component.name}
                      </Typography>
                      <DragIndicatorIcon sx={{ fontSize: 15, opacity: 0.35, flexShrink: 0 }} />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Stack spacing={0.75}>
                  {liveTemplates.map((template: FormTemplate) => (
                    <Card
                      key={template.id}
                      sx={{
                        height: 68, p: 1, cursor: 'pointer', borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`, boxShadow: 'none',
                        display: 'flex', alignItems: 'center', gap: 1,
                        transition: 'border-color 80ms ease',
                        '&:hover': { borderColor: theme.palette.success.main },
                      }}
                      onClick={() => addFormTemplate(template)}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.success.main, flexShrink: 0 }}>
                        {getIconComponent(template.icon)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                          {template.displayName ?? template.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                          <Chip label={t('processes:wizardForm.componentsCount', { count: template.components.length })} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.625rem', borderRadius: 1 }} />
                          {template.category && <Chip label={translateTemplateDomain(t, template.category)} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.625rem', borderRadius: 1 }} />}
                        </Stack>
                      </Box>
                      <AddIcon sx={{ color: theme.palette.success.main, fontSize: 18, flexShrink: 0 }} />
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Form Design Canvas */}
        <Box sx={{ minWidth: 0, height: { xs: 'auto', md: DESIGNER_STAGE_HEIGHT.height }, minHeight: { xs: 420, md: DESIGNER_STAGE_HEIGHT.minHeight } }}>
          <Paper sx={{ height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, mb: 1.5, px: 1.5, pt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>{t('processes:wizardForm.formCanvas')}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {/* HOW TO REACH THE PROPERTIES PANEL. It is rich — label, placeholder, options,
                    required — and it does not exist in the DOM until a field is selected, because
                    the grid only grows a third track then. So an admin who has not clicked a row
                    has no way to learn the panel is there; the owner read that as "不能配置属性"
                    while the editor worked (2026-09-01).
                    A hint beside the count rather than a permanently mounted panel: mounting it
                    empty costs the canvas ~200px of width, which is the constraint that put the
                    panel behind a selection in the first place. */}
                {formComponents.length > 0 && !selectedId && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('processes:wizardForm.selectToEditHint')}
                  </Typography>
                )}
                <Chip label={t('processes:wizardForm.componentsCount', { count: formComponents.length })} variant="outlined" size="small" color="primary" sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />
              </Stack>
            </Box>
            <DragDropContext
              onDragStart={() => { draggingRef.current = true; }}
              onDragEnd={(result) => {
                draggingRef.current = false;
                // The Draggable unit is a BLOCK, so `source.index` is a block index.
                // Handing it to the old flat single-item splice would move one field
                // out of its group — the opposite of 拖动只能整组.
                if (!result.destination) return;
                if (result.source.index === result.destination.index) return;
                onReorderSteps(
                  reorderBlocks(formComponents, result.source.index, result.destination.index)
                    .map((step) => step.id),
                );
              }}
            >
              {/* THE DROPPABLE IS ALWAYS MOUNTED, and the empty state renders INSIDE it.
                *
                * It used to be the second arm of `formComponents.length === 0 ? … : …`, which is two
                * defects in one shape:
                *
                *  1. `Invariant failed: Cannot find droppable entry with id [canvas]` becomes
                *     reachable. react-beautiful-dnd registers a droppable on MOUNT and looks it up
                *     by id at drag start, so any state that empties the canvas unregisters `canvas`
                *     — and a drag begun before the remount settles throws. Reported 2026-09-01.
                *     A jsdom render proves registration holds across selection
                *     (`canvasDragRegistration.test.tsx`), which is why this is the STRUCTURAL fix
                *     rather than a guess at the trigger: with the droppable unconditional the error
                *     is unreachable instead of unlikely. An always-mounted empty droppable is also
                *     what rbd's own docs prescribe — it is how you drop into an empty list.
                *
                *  2. the HTML5 drop target lived only on the empty arm. The palette drags with
                *     `dataTransfer` (a different mechanism from rbd), so dragging a component from
                *     the library onto a canvas that ALREADY had fields had no `onDrop` at all — it
                *     worked exactly once, when the canvas was empty. The handlers now sit on the
                *     droppable itself and cover both states. */}
              <Droppable droppableId="canvas">
                  {(provided, snapshot) => (
                    <Box
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      onDrop={(e) => {
                        e.preventDefault();
                        const data = e.dataTransfer.getData('application/json');
                        if (data) onAdd(JSON.parse(data));
                      }}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                      sx={{
                        flex: 1, overflow: 'auto', borderRadius: 2,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: snapshot.isDraggingOver ? 'action.hover' : '#fafafa',
                        // FILLS ITS TRACK. No `maxWidth`, no `mx: 'auto'` — measured, because both
                        // were wrong and in opposite directions.
                        //
                        // The track is `minmax(0, 1fr)` between a 240px palette and an optional 260px
                        // inspector, so on the 13" baseline it is 724px with nothing selected and
                        // 452px with a field selected (see FORM_BUILDER_COLUMNS). Against that, a
                        // 600px cap does two different wrong things: at 724 it leaves 124px of dead
                        // space on the right, and at 452 it does nothing at all. The owner read the
                        // wide case as the canvas needing to be centred (2026-09-01: Form Canvas
                        // 是不是可以居中？).
                        //
                        // Centring would have been the wrong fix: this panel's own header — "Form
                        // Canvas" and the count chip — spans the full width, so a centred body puts a
                        // second origin INSIDE one card (svdy-ui §15). Filling the track removes the
                        // dead space AND keeps the body on the header's left edge, which is the one
                        // axis this panel already had.
                        //
                        // The cap was protecting against a stretched 12-column grid putting a Half
                        // field far from its label. The PANEL is what bounds that now, and the panel
                        // is bounded by the layout's own arithmetic — a cap inside a cap is a second
                        // edge nobody asked for.
                        //
                        // AMENDED 2026-09-02, TWICE, and the second amendment is the one that matters.
                        //
                        // "The panel is bounded by the layout's own arithmetic" is true at the 13"
                        // baseline and FALSE on a large display: the column was `1fr`, so nothing
                        // bounded it and at ~2300px a row's label sat ~1900px from its type summary.
                        //
                        // The first repair capped THIS box at `PORTAL_FORM_MAX_WIDTH` and was worse
                        // than no cap: the PANEL still filled the track, so a 720px canvas sat inside
                        // a 1720px card with a thousand pixels of empty white beside it and the
                        // header's count chip stranded at the far right. The cap now lives on the
                        // COLUMN (see `FORM_BUILDER_COLUMNS`), so the panel, its header and this grid
                        // are all 720 and share one right edge.
                        //
                        // So this box fills its panel again, and the original note's last clause is
                        // vindicated rather than overturned: a cap inside a cap really is a second
                        // edge nobody asked for. It was the LEVEL that was wrong, not the sentence.
                        p: 1.5, width: '100%',
                        display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: CANVAS_ROW.blockGap,
                        alignContent: 'start',
                      }}
                    >
                      {formComponents.length === 0 && (
                <Box
                          sx={{
                            gridColumn: 'span 12',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            minHeight: 240,
                            bgcolor: theme.palette.grey[50], borderRadius: 2,
                            border: `2px dashed ${theme.palette.grey[300]}`, p: 4,
                          }}
                        >
                          <Avatar sx={{ width: 48, height: 48, bgcolor: theme.palette.grey[100], mb: 2 }}>
                            <ListAltIcon sx={{ fontSize: 24, color: theme.palette.grey[400] }} />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, textAlign: 'center' }}>
                            {t('processes:wizardForm.dragHere')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 280 }}>
                            {t('processes:wizardForm.dragHint')}
                          </Typography>
                          {/*
                            No button here, and that is a REVERSAL of this morning's fix.

                            While the library was a modal drawer, the empty state had to carry the
                            opener — the copy pointed at a left-hand panel that did not exist and the
                            only way in was a small button in the far corner. The owner then rejected
                            the drawer itself, so the palette is docked again and permanently visible
                            two columns to the left. An "Add field" button in the middle of the canvas
                            would now be a second entrance to something already on screen, and
                            `dragHint` can once again say plainly where the library is.
                          */}
                        </Box>
                      )}
                      {toCanvasBlocks(formComponents).map((block, blockIndex) => {
                        const grouped = block.groupId !== null;
                        // A group spans the full row: its members keep their own colSpan
                        // INSIDE the container, so a 6+6 pair still sits side by side.
                        const span = grouped ? 12 : (block.steps[0].properties.colSpan || 12);
                        const hovered = block.steps.some((s) => hoveredComponentId === s.id);
                        const selectedInBlock = block.steps.some((s) => selectedId === s.id);
                        return (
                        <Draggable key={block.key} draggableId={block.key} index={blockIndex}>
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={provided.draggableProps.style}
                              sx={{ gridColumn: `span ${span}` }}
                              onMouseEnter={() => setHoveredComponentId(block.steps[0].id)}
                              onMouseLeave={() => setHoveredComponentId(null)}
                            >
                              {grouped ? (
                                /* ONE bordered container per group, and the border REPLACES the
                                   per-row border rather than nesting inside it — a card inside a
                                   card stacks a second competing edge and pushes content to a
                                   third left offset (svdy-ui §15). */
                                <Box
                                  data-form-group={block.groupId}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: selectedInBlock ? 'primary.main' : 'divider',
                                    borderRadius: 1.5,
                                    bgcolor: '#fff',
                                    boxShadow: snapshot.isDragging ? theme.shadows[8] : 'none',
                                    transition: 'border-color 80ms ease',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75, pt: 0.5, pb: 0.25 }}>
                                    <Box {...provided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', pr: 0.5, cursor: 'grab' }}>
                                      <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                    </Box>
                                    {/* THE TEMPLATE'S NAME — as authoring provenance, not a heading.
                                        Two owner reports, two days apart, pulling opposite ways:

                                        2026-08-27: 添加到的组件模版不能有标题吧？ — and the reason
                                        recorded then still holds: a HEADING here reads as a section
                                        title the requester will see, and the engine renders no such
                                        thing.

                                        2026-09-01: 组件模版点击依旧不生效 — while the canvas in the
                                        screenshot held exactly the 3 + 5 fields two templates had
                                        just inserted. The click worked. What failed is that an
                                        anonymous "3 components" block cannot be recognised as the
                                        General Approval the admin just clicked, so a successful
                                        insert is indistinguishable from a dead button.

                                        Resolved by WHERE and HOW, not by whether: this is the
                                        chrome row that already holds the drag handle, the count and
                                        the delete — a row the requester never sees — and it is set
                                        as muted caption text, not as a title. `group_label` was
                                        already stored for exactly this; only the render was missing. */}
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                      {block.label && (
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          title={block.label}
                                          sx={{
                                            display: 'block',
                                            color: 'text.secondary',
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {t('processes:wizardForm.fromTemplate', { name: block.label })}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Chip
                                      label={t('processes:wizardForm.componentsCount', { count: block.steps.length })}
                                      size="small" variant="outlined"
                                      sx={{ height: 18, fontSize: '0.625rem', borderRadius: 1, mr: 0.5 }}
                                    />
                                    {/* The group's ONLY delete. Members have none: 整组删除. */}
                                    <Box
                                      role="button"
                                      aria-label={t('processes:wizardForm.deleteGroup')}
                                      sx={{
                                        flexShrink: 0, display: 'flex', alignItems: 'center',
                                        borderRadius: '50%', p: '3px', cursor: 'pointer',
                                        visibility: hovered ? 'visible' : 'hidden',
                                        backgroundColor: 'rgba(0,0,0,0.04)',
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveMany(groupMemberIds(formComponents, block.steps[0].id));
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 14, color: 'text.secondary', display: 'block' }} />
                                    </Box>
                                  </Box>
                                  {/* `rowGap: 0`: members stack FLUSH and a hairline separates
                                      them (owner 2026-08-27 — 下划线做隔离和区分). A gap AND a
                                      divider is separation twice. `columnGap` stays, so a 6+6
                                      colSpan pair still has a gutter between them. */}
                                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', columnGap: 1, rowGap: 0, px: CANVAS_ROW.padY, pb: CANVAS_ROW.padY }}>
                                    {block.steps.map((step, memberIndex) => (
                                      /* SELECTABLE — and the two objections that made it not
                                         selectable are answered rather than overruled.
                                         
                                         2026-08-27: 整组删除，不支持表单内组件单独编辑，拖动
                                         也只能是整个组件模板拖动和删除. DELETE and DRAG remain
                                         whole-group: no handle, no delete on a member. Those
                                         halves of that instruction are untouched.
                                         
                                         2026-08-28 withdrew a WIDTH control from here, for two
                                         reasons found by using it:
                                           (a) width became settable in TWO places for one
                                               decision — here and on the template — and the
                                               canvas copy does not travel back, so one template
                                               inserted twice could differ with nothing on screen
                                               to explain it;
                                           (b) a selected member showed no selection of its own
                                               while the panel dropped nine controls, which reads
                                               as broken rather than as constrained.
                                         
                                         (a) is about WIDTH SPECIFICALLY: `colSpan` is a property
                                         of the TEMPLATE ROW, while `label`, `options`, `required`
                                         and `placeholder` are properties of the FIELD, copied into
                                         this form's schema exactly as a loose field's are. So the
                                         width control stays off for a member — see the panel — and
                                         the rest is offered, which is the same set a loose field
                                         gets and which nobody has ever objected to editing.
                                         
                                         (b) was an implementation defect, not an argument: a
                                         member now shows its own selected state, and the panel
                                         names the template it came from so the admin knows the
                                         change reaches THIS form and not the template.
                                         
                                         Reported three times (2026-09-01): 无法选中模版中的单个
                                         组件，设置属性. */
                                      <Box
                                        key={step.id}
                                        data-group-member={step.id}
                                        onClick={() => {
                                          // A drag that started on this row ends with a click here.
                                          // Selecting would re-render the canvas from inside the drag
                                          // and churn the draggable ids — see `draggingRef`.
                                          if (draggingRef.current) return;
                                          onSelect(selectedId === step.id ? null : step.id);
                                        }}
                                        sx={{
                                          gridColumn: `span ${step.properties.colSpan || 12}`,
                                          minWidth: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          borderRadius: 1,
                                          // ITS OWN SELECTED STATE — objection (b). Without it the
                                          // panel changed and the canvas did not, so the click read
                                          // as having gone somewhere else.
                                          bgcolor: selectedId === step.id
                                            ? 'rgba(0,113,227,0.08)' : 'transparent',
                                          transition: 'background-color 80ms ease',
                                          '&:hover': {
                                            bgcolor: selectedId === step.id
                                              ? 'rgba(0,113,227,0.12)' : 'rgba(0,0,0,0.03)',
                                          },
                                          // Height parity with a loose row, from ONE token. A member
                                          // used to carry neither the card's padding nor its borders,
                                          // which is the whole 14px the owner measured.
                                          py: CANVAS_ROW.padY,
                                        }}
                                      >
                                        {/* The SAME three slots as a loose row — handle,
                                            content, delete — with the outer two RESERVED and
                                            inert. Owner 2026-08-27: a grouped field offers no
                                            control of its own. But dropping the slots put its
                                            text on a second axis: measured off the owner's
                                            screenshot, loose rows inked at x=293..822 and
                                            grouped members at x=300..846 — same content, two
                                            origins (svdy-ui §15). Reserving the space keeps one
                                            axis while offering nothing. `aria-hidden` because a
                                            spacer is not a control. */}
                                        <Box
                                          aria-hidden
                                          sx={{ display: 'flex', alignItems: 'center', pr: 0.5, visibility: 'hidden', flexShrink: 0 }}
                                        >
                                          <DragIndicatorIcon sx={{ fontSize: 16 }} />
                                        </Box>
                                        {/* The hairline sits on the CONTENT slot, not on the row:
                                            owner 2026-08-27 — 这个下划线太长了. Spanning the row ran it
                                            under the reserved handle and delete slots, so it was
                                            ~40px wider than the text it separates and read as a
                                            full-width rule rather than a separator. The last member
                                            keeps the same 1px BOX, transparent, or the group's final
                                            row is a pixel shorter than its siblings. */}
                                        <Box
                                          sx={{
                                            flexGrow: 1,
                                            minWidth: 0,
                                            py: CANVAS_ROW.contentPadY,
                                            borderBottom: '1px solid',
                                            borderColor: memberIndex === block.steps.length - 1
                                              ? 'transparent'
                                              : 'divider',
                                          }}
                                        >
                                          {renderCanvasComponent(step, formComponents.indexOf(step), true)}
                                        </Box>
                                        <Box
                                          aria-hidden
                                          sx={{ flexShrink: 0, ml: 0.5, display: 'flex', alignItems: 'center', p: '3px', visibility: 'hidden' }}
                                        >
                                          <DeleteIcon sx={{ fontSize: 14, display: 'block' }} />
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              ) : (
                                <Card
                                  sx={{ display: 'flex', alignItems: 'center', p: CANVAS_ROW.padY, borderRadius: 1.5, border: '1px solid', borderColor: selectedId === block.steps[0].id ? 'primary.main' : 'divider', boxShadow: snapshot.isDragging ? theme.shadows[8] : 'none', transition: 'border-color 80ms ease', position: 'relative', '&:hover': { borderColor: selectedId !== block.steps[0].id ? theme.palette.grey[400] : 'primary.main' }, cursor: 'pointer', bgcolor: '#fff' }}
                                  onClick={() => onSelect(block.steps[0].id)}
                                >
                                  <Box {...provided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pr: 0.5, cursor: 'grab' }}>
                                    <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                  </Box>
                                  <Box sx={{ flexGrow: 1, minWidth: 0, py: CANVAS_ROW.contentPadY }}>
                                    {/* The FLAT field position, not `blockIndex`. A block is a
                                        loose row OR a whole group, so after two groups the next
                                        loose row numbered itself 6 while sitting under item 11
                                        (owner screenshot 2026-08-27 23:05). Group members already
                                        used the flat index; two numbering rules in one list is
                                        the §10.5b shape at its plainest. */}
                                    {renderCanvasComponent(block.steps[0], formComponents.indexOf(block.steps[0]), false)}
                                  </Box>
                                  {/* IN-FLOW, not absolute. Overlaying the row covered the type
                                      label — the owner's screenshot shows the trash icon sitting on
                                      top of "Date". `visibility` rather than conditional render so
                                      the slot is always reserved and nothing shifts on hover. */}
                                  <Box
                                    role="button"
                                    aria-label={t('common:actions.delete')}
                                    sx={{
                                      flexShrink: 0, ml: 0.5, display: 'flex', alignItems: 'center',
                                      borderRadius: '50%', p: '3px', cursor: 'pointer',
                                      visibility: hoveredComponentId === block.steps[0].id ? 'visible' : 'hidden',
                                      backgroundColor: 'rgba(0,0,0,0.04)',
                                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                                    }}
                                    onClick={(e) => { e.stopPropagation(); onRemove(block.steps[0].id); }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 14, color: 'text.secondary', display: 'block' }} />
                                  </Box>
                                </Card>
                              )}
                            </Box>
                          )}
                        </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>

            </DragDropContext>
          </Paper>
        </Box>

        {/* Properties Panel */}
        {/* Only a COLUMN when there is something to inspect. `FORM_BUILDER_COLUMNS` has two
            tracks until a field is selected, so rendering this unconditionally would push a
            third child into a two-track grid — and it is what buys the canvas back to 724px,
            clearing the 720px form it renders. */}
        {selectedComponentData && (
        <Box sx={{ minWidth: 0, height: { xs: 'auto', md: DESIGNER_STAGE_HEIGHT.height }, minHeight: { md: DESIGNER_STAGE_HEIGHT.minHeight } }}>
            <Paper sx={{ p: 1.5, height: { xs: 'auto', md: '100%' }, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>{t('processes:wizardForm.propertiesTitle')}</Typography>
                {selectedId && <IconButton size="small" onClick={() => onSelect(null)}><CloseIcon /></IconButton>}
              </Box>
              {/* WHERE THIS FIELD CAME FROM, and what an edit here reaches.
                *
                * A template's fields are COPIED into this form's schema on insert, so editing one
                * changes this form only. Without saying so, an admin editing a field inside
                * "From IT Access Request" would reasonably believe they were editing the template —
                * and would be surprised twice: once when the next process does not have the change,
                * and once when this one does. */}
              {selectedGroupLabel && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mb: 1.5, flexShrink: 0 }}
                >
                  {t('processes:wizardForm.editingTemplateField', { name: selectedGroupLabel })}
                </Typography>
              )}
              {selectedId && selectedComponentData ? (
              <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                <Stack spacing={1.5}>
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'label') && (
                    <TextField label={t('processes:wizardForm.labelText')} value={selectedComponentData.properties.label || ''} onChange={(e) => onUpdate(selectedId, 'label', e.target.value)} fullWidth variant="outlined" size="small" />
                  )}
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'placeholder') && (
                    <TextField label={t('processes:wizardForm.placeholderHelper')} value={selectedComponentData.properties.placeholder || ''} onChange={(e) => onUpdate(selectedId, 'placeholder', e.target.value)} fullWidth variant="outlined" size="small" />
                  )}
                  {/*
                    No `maxLength` / `min` / `max` / `maxSize` editors here, deliberately
                    (2026-08-24). The backend enforces all four, but the bounds now come from
                    TYPE-LEVEL defaults in `form_validation.py`, not from an admin.

                    Each of these asked a shop owner to invent a number they have no basis for:
                    whether a justification needs 500 or 2000 characters (they already answered
                    "short vs long" by choosing Text Field versus Multi-line Text), or whether a
                    receipt needs 3 MB or 5 MB (unanswerable without knowing that the mobile client
                    downscales before upload and the web file picker does not). `number_field` also
                    carried exactly ONE property before this, so min+max tripled its config
                    surface. Every one of the four was a new way for an admin to wedge their own
                    form — the fail-open branch for `min > max` in `normalize_form_data` exists
                    precisely because the control was dangerous in untrained hands.

                    `allowedTypes` below is kept on purpose: "does this field take a PDF or also a
                    photo?" is a business question an SMB owner CAN answer, and it is enforced in
                    `create_attachment_upload_intent`. That is the line between the two groups.

                    A value supplied through the API is still honoured, because
                    `FormComponent.properties` is free-form and the backend reads whatever is
                    there. Integrators lose nothing; only the UI stops asking.
                  */}
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'allowedTypes') && (
                    <FormControl fullWidth size="small">
                      <InputLabel shrink>{t('processes:wizardForm.allowedFileTypes')}</InputLabel>
                      <Select
                        multiple
                        notched
                        displayEmpty
                        label={t('processes:wizardForm.allowedFileTypes')}
                        value={attachmentFormats(selectedComponentData.properties.allowedTypes)}
                        onChange={(event) => onUpdate(selectedId, 'allowedTypes', event.target.value)}
                        renderValue={(selected) => (selected as string[])
                          .map((value) => t(`processes:wizardForm.fileFormats.${value}`))
                          .join(', ')}
                      >
                        {/* Bounded to what the runtime accepts. A free-text field let an admin
                            type `docx`, which the upload endpoint rejects — the promise was made
                            in one place and broken in another. */}
                        {PROCESS_ATTACHMENT_FORMATS.map((format) => (
                          <MenuItem key={format.value} value={format.value}>
                            {t(`processes:wizardForm.fileFormats.${format.value}`)}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t('processes:wizardForm.allowedFileTypesHelper')}
                      </Typography>
                    </FormControl>
                  )}
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'currency') && (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('processes:wizardForm.currencySymbol')}</InputLabel>
                      <Select
                        label={t('processes:wizardForm.currencySymbol')}
                        value={String(selectedComponentData.properties.currency || 'USD').toUpperCase()}
                        onChange={(e) => onUpdate(selectedId, 'currency', e.target.value)}
                      >
                        {['USD', 'CNY', 'EUR', 'GBP', 'JPY', 'KRW', 'BRL', 'CAD', 'AUD', 'CHF', 'SGD'].map((code) => (
                          <MenuItem key={code} value={code}>{code}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'options') && (
                    <FormOptionsEditor
                      key={selectedId}
                      fieldId={selectedId}
                      options={selectedComponentData.properties.options}
                      onChange={(options) => onUpdate(selectedId, 'options', options)}
                    />
                  )}
                  {/* `text`, not `content`. `validate_form_schema` requires
                      `properties.text` on a `static_text` field and all three renderers
                      read it, so the old key meant a Guidance Note's words could only
                      come from the SEED: an admin typed into `content`, publish refused
                      the empty note, and nothing on screen explained why. One concept,
                      one key (svdy-rules §10.5b). */}
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'text') && (
                    <TextField
                      label={t('processes:wizardForm.noteText')}
                      value={selectedComponentData.properties.text || ''}
                      onChange={(e) => onUpdate(selectedId, 'text', e.target.value)}
                      helperText={t('processes:wizardForm.noteTextHelper')}
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {/* A date field that also asks for the hour. `date.fromisoformat` accepts
                      only `YYYY-MM-DD`, so "leave starting 25 Aug 14:00" was not
                      expressible — this field's only property was `required`. */}
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'include_time') && (
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={selectedComponentData.properties.include_time === true}
                          onChange={(e) => onUpdate(selectedId, 'include_time', e.target.checked)}
                        />
                      )}
                      label={t('processes:wizardForm.includeTime')}
                    />
                  )}
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'multiline') && (
                    <FormControlLabel control={<Switch checked={selectedComponentData.properties.multiline || false} onChange={(e) => onUpdate(selectedId, 'multiline', e.target.checked)} />} label={t('processes:wizardForm.multilineInput')} />
                  )}
                  {Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'required') && (
                    <FormControlLabel control={<Switch checked={selectedComponentData.properties.required || false} onChange={(e) => onUpdate(selectedId, 'required', e.target.checked)} />} label={t('processes:wizardForm.requiredField')} />
                  )}
                  {/* WIDTH IS NOT OFFERED FOR A GROUP MEMBER (2026-08-28, and it still holds).
                    *
                    * `colSpan` for a template field is set on the TEMPLATE row, where the change
                    * also reaches every future insert. Offering it here too would make one decision
                    * settable in two places, with the canvas copy never travelling back — so one
                    * template inserted twice could differ and nothing on screen would say why.
                    *
                    * This is the one property that behaves that way, which is why the rest of the
                    * panel IS offered to a member. */}
                  {!selectedGroupLabel
                    && Object.prototype.hasOwnProperty.call(selectedComponentData.properties, 'colSpan') && (
                    /* One click, not two. It was a two-option Select
                       (Half width / Full width) — already a choice rather than a
                       column count, so this is alignment with Jotform's `Shrink`
                       toggle rather than a defect fix. The STORED value is
                       unchanged: still 6 or 12, which the preview and the worker
                       portal both read as a 12-column grid. */
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={(selectedComponentData.properties.colSpan ?? 12) === 6}
                          onChange={(_, checked) => onUpdate(selectedId, 'colSpan', checked ? 6 : 12)}
                        />
                      )}
                      label={t('processes:wizardForm.halfWidthToggle')}
                    />
                  )}
                </Stack>
              </Box>
              ) : (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                <SettingsIcon sx={{ fontSize: 44, color: 'action.disabled' }} />
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 2, fontSize: '0.8125rem', lineHeight: 1.5 }}>
                  {t('processes:wizardForm.clickToEdit')}
                </Typography>
              </Box>
              )}
            </Paper>
          </Box>
        )}
    </Box>
  );
};

export default ProcessFormDesign;
export { ProcessFormDesign as WizardFormBuilder };
